import tenantConfig from "@tenant/config.json";

export type BrandId =
  | "realvirtual"
  | "synapta"
  | "back9"
  | "fleming"
  | "mega"
  | "golfexpress"
  | "eurodesign"
  | "poliformas"
  | "mepiel"
  | "back9test";

export type ThemeVariant = string;

export interface LogoSet {
  squareLight: string;
  squareDark: string;
  rectLight: string;
  rectDark: string;
}
export type LogoUseCase = "auth" | "header";
export type LogoConfig = Record<LogoUseCase, LogoSet>;

export interface BrandingConfig {
  marketingUrl?: string;
  loginUrl?: string;
  primary?: string;
  secondary?: string;
  accent1?: string;
}

export interface BrandConfig {
  id: BrandId;
  displayName: string;
  themeVariant: ThemeVariant;
  logos: LogoConfig;
  agentName: string;
  favicon?: string;
  branding?: BrandingConfig;
}

interface TenantAssetMap {
  auth: {
    squareLight: string;
    squareDark: string;
    rectLight?: string;
    rectDark?: string;
  };
  header: {
    squareLight?: string;
    squareDark?: string;
    rectLight: string;
    rectDark: string;
  };
}

const TENANT_LOGOS: Record<string, TenantAssetMap> = {
  realvirtual: {
    auth: {
      squareLight: "splashscreen-rv-light.png",
      squareDark: "splashscreen-rv-allwhite.png",
      rectLight: "header-rv-light.png",
      rectDark: "header-rv-allwhite.png",
    },
    header: {
      squareLight: "splashscreen-rv-light.png",
      squareDark: "splashscreen-rv-allwhite.png",
      rectLight: "header-rv-light.png",
      rectDark: "header-rv-allwhite.png",
    },
  },
  synapta: {
    auth: {
      squareLight: "synapta-square-grey.png",
      squareDark: "synapta-square-white.png",
      rectLight: "synapta-rectangular-grey.png",
      rectDark: "synapta-rectangular-white.png",
    },
    header: {
      squareLight: "synapta-square-grey.png",
      squareDark: "synapta-square-white.png",
      rectLight: "synapta-rectangular-grey.png",
      rectDark: "synapta-rectangular-white.png",
    },
  },
  back9: {
    auth: {
      squareLight: "back9-horizontal-color.png",
      squareDark: "back9-horizontal-white.png",
      rectLight: "back9-horizontal-color.png",
      rectDark: "back9-horizontal-white.png",
    },
    header: {
      squareLight: "back9-horizontal-color.png",
      squareDark: "back9-horizontal-white.png",
      rectLight: "back9-horizontal-color.png",
      rectDark: "back9-horizontal-white.png",
    },
  },
  fleming: {
    auth: {
      squareLight: "FLEMING-MX-LOGO.png",
      squareDark: "Logo-White-fleming.svg",
      rectLight: "FLEMING-MX-LOGO.png",
      rectDark: "Logo-White-fleming.svg",
    },
    header: {
      squareLight: "FLEMING-MX-LOGO.png",
      squareDark: "Logo-White-PNG.png",
      rectLight: "FLEMING-MX-LOGO.png",
      rectDark: "Logo-White-fleming.svg",
    },
  },
  mega: {
    auth: {
      squareLight: "logosmega/Logo Megaservicio 2025-cuadro color.png",
      squareDark: "logosmega/Logo Megaservicio 2025-cuadro blanco.png",
      rectLight: "logosmega/Logo Megaservicio 2025_Horizontal color.png",
      rectDark: "logosmega/Logo Megaservicio 2025_Horizontal color.png",
    },
    header: {
      squareLight: "logosmega/Logo Megaservicio 2025-cuadro color.png",
      squareDark: "logosmega/Logo Megaservicio 2025-cuadro blanco.png",
      rectLight: "logosmega/Logo Megaservicio 2025_Horizontal color.png",
      rectDark: "logosmega/Logo Megaservicio 2025_Horizontal color.png",
    },
  },
  golfexpress: {
    auth: {
      squareLight: "golfexpress.png",
      squareDark: "golfexpressblanco.png",
      rectLight: "golfexpress.png",
      rectDark: "golfexpressblanco.png",
    },
    header: {
      squareLight: "golfexpress.png",
      squareDark: "golfexpressblanco.png",
      rectLight: "golfexpress.png",
      rectDark: "golfexpressblanco.png",
    },
  },
};

const getLogos = (id: string): LogoConfig => {
  const mapping = TENANT_LOGOS[id] || TENANT_LOGOS["synapta"];
  const getPath = (filename?: string) =>
    filename
      ? `/tenants/${id}/assets/${filename}`
      : `/tenants/${id}/assets/logo.png`;

  return {
    auth: {
      squareLight: getPath(mapping.auth.squareLight),
      squareDark: getPath(mapping.auth.squareDark),
      rectLight: getPath(mapping.auth.rectLight || mapping.auth.squareLight),
      rectDark: getPath(mapping.auth.rectDark || mapping.auth.squareDark),
    },
    header: {
      squareLight: getPath(mapping.header.squareLight || mapping.auth.squareLight),
      squareDark: getPath(mapping.header.squareDark || mapping.auth.squareDark),
      rectLight: getPath(mapping.header.rectLight),
      rectDark: getPath(mapping.header.rectDark),
    },
  };
};

export const CURRENT_BRAND: BrandConfig = {
  id: tenantConfig.id as BrandId,
  displayName: tenantConfig.displayName,
  themeVariant: tenantConfig.themeVariant as ThemeVariant,
  agentName: tenantConfig.agentName,
  favicon: `/tenants/${tenantConfig.id}/assets/${tenantConfig.faviconFile || "favicon.ico"}`,
  logos: getLogos(tenantConfig.id),
  branding: tenantConfig.branding,
};

export const BRAND_ID = CURRENT_BRAND.id;
